/**
 * Import Profile Registry
 *
 * Each profile defines UI metadata, source expectations, guided walkthrough
 * content, and post-download MV import instructions.
 */

export const PROFILES = {
  'gl9300c-budget': {
    name: 'Budget Upload',
    description: 'Transform budget data into GL9300C Excel format with keyword header blocks, period columns, and amount scaling.',
    icon: '💰',
    interfaceId: 'GL9300C',
    controlFile: 'GL9300C.mvl',
    outputPrefix: 'GL9300C_Budget_Import',
    bestFor: 'Annual or periodic GL budget loads from MV budget detail exports',
    sourceColumns: ['unit', 'account', 'jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
    optionalColumns: ['company_id', 'project_type', 'project_id', 'data_type', 'remarks'],
    requiredFields: ['unit', 'account'],
    prepareChecklist: [
      'Export budget detail from MV (one row per account per month) or use the template',
      'Pivot monthly amounts into columns JAN–DEC if your export is long-format',
      'Confirm company IDs (71, 77, 81, 90) if loading multiple entities',
      'Flip revenue accounts (5xxxxx) to positive — MV auto-negates on import',
      'Remove known bad accounts: 700073, 700090, 703247, 700556, 700100',
    ],
    keyRules: [
      { label: 'Revenue sign', detail: '5xxxxx accounts must be positive in the file. MV negates them automatically.' },
      { label: 'Amount scaling', detail: 'Values are multiplied ×100 for implied decimals ($16,800 → 1680000).' },
      { label: 'Period headers', detail: 'Output uses MON-YY format (JAN-26, FEB-26, …) in columns I–T.' },
      { label: 'Multi-company', detail: 'Each company gets its own Budget ID / Company ID header block on one sheet.' },
    ],
    columnGuide: [
      { column: 'unit', description: 'Business unit ID (e.g. 80)', required: true },
      { column: 'account', description: '6-digit GL account number', required: true },
      { column: 'jan … dec', description: 'Monthly budget amounts in dollars', required: true },
      { column: 'company_id', description: 'Entity code (71, 77, 81, 90) for multi-company files', required: false },
    ],
    outputPreview: `Budget ID | 2026BU
Description | FY2026 Budget
Company ID | 81
Type | R
Periods | 12

Unit | Account | ... | JAN-26 | FEB-26 | ... | DEC-26
80   | 701180  | ... | 1680000 | 1680000 | ... | 1680000`,
    importSteps: [
      { title: 'Copy file to server', detail: 'Place the .xlsx or .txt file in <mv_home>/<oracle_sid>/interface/in/glentry' },
      { title: 'Open Budget Interface', detail: 'In MV: GL Manager Functions → Budget Interface Data Corrections' },
      { title: 'Submit batch job', detail: 'Run the interface using control file GL9300C.mvl. Choose "Validate Only" first if unsure.' },
      { title: 'Review batch log', detail: 'Check for ORA-20001 (invalid account) errors. Report new bad accounts for the exclusion list.' },
    ],
  },

  'gl8610xl-je': {
    name: 'Manual Journal Entry',
    description: 'Convert a simple JE spreadsheet (unit, account, debit, credit) into GL8610XL keyword-based format.',
    icon: '📋',
    interfaceId: 'GL8610XL',
    controlFile: 'GL8610XL',
    outputPrefix: 'GL8610XL_JE_Import',
    bestFor: 'Month-end accruals, adjustments, and manual journal entries',
    sourceColumns: ['unit', 'account', 'debit', 'credit', 'description'],
    optionalColumns: ['company_id', 'accounting_date', 'entry_type', 'project_type', 'project_id', 'amount'],
    requiredFields: ['unit', 'account'],
    prepareChecklist: [
      'List each JE line with unit, account, debit OR credit (not both on same row) — or a single signed Amount column',
      'Confirm debits and credits (or signed amounts) balance to zero across the entire file',
      'Set company ID and accounting date (or add as columns in your source)',
      'Use 6-digit account numbers without formatting characters',
      'Add descriptions/remarks for audit trail on each line',
    ],
    keyRules: [
      { label: 'Balance', detail: 'Total debits must equal total credits. The tool blocks export if out of balance.' },
      { label: '$0 lines', detail: 'Lines worth $0 are dropped automatically (the count is shown on the review screen). A line with a blank or unreadable amount is kept and flagged as an error instead.' },
      { label: 'Sign convention', detail: 'Output uses positive debits and negative credits in a single Amount column.' },
      { label: 'Sticky headers', detail: 'Company ID, date, and remarks persist until redeclared in the output file.' },
      { label: 'Date format', detail: 'Accounting dates output as YYYYMMDD (e.g. 20260131).' },
    ],
    columnGuide: [
      { column: 'unit', description: 'Business unit ID', required: true },
      { column: 'account', description: 'GL account number', required: true },
      { column: 'debit', description: 'Debit amount (leave blank if credit)', required: true },
      { column: 'credit', description: 'Credit amount (leave blank if debit)', required: true },
      { column: 'amount', description: 'Alternative to debit/credit: single signed amount column (+ debit, − credit)', required: false },
      { column: 'description', description: 'Line-level remarks', required: false },
    ],
    outputPreview: `Company ID | 81
Accounting Date | 20260131
Entry Type | JE
Remarks | January 2026 Accruals
Layout | Unit | Account | Amount | Remarks
Detail | 80 | 701180 | 5000 | Accrued expense
Detail | 80 | 200100 | -5000 | Offsetting entry`,
    importSteps: [
      { title: 'Copy file to server', detail: 'Place the .xlsx or .txt file in <mv_home>/<oracle_sid>/interface/in/glentry' },
      { title: 'Open GL Interface', detail: 'In MV: GL Manager Functions → GL Interface Data Corrections' },
      { title: 'Submit batch job', detail: 'Run using control file GL8610XL. Start with "Validate Only" to catch errors early.' },
      { title: 'Review batch log', detail: 'Fix any ORA-20001 account errors in source data and re-submit.' },
    ],
  },

  'gl8610xl-payroll': {
    name: 'Payroll JE (iSolved)',
    description: 'Transform iSolved _GL_Report_ Excel exports into GL8610XL journal entry format. Reads the tabular Sheet1 data — or a sheet already staged in keyword format (Layout + Detail rows) — and applies keyword headers from the file.',
    icon: '👥',
    interfaceId: 'GL8610XL',
    controlFile: 'GL8610XL',
    outputPrefix: 'GL8610XL_Payroll_Import',
    bestFor: 'iSolved payroll GL reports (_GL_Report_*.xlsx) with Date, Unit, Account, Amount columns, or a staged keyword sheet (Layout + Detail rows)',
    sourceColumns: ['date', 'unit', 'account', 'amount', 'description'],
    optionalColumns: ['company_id', 'project_type', 'project_id'],
    requiredFields: ['unit', 'account', 'amount'],
    prepareChecklist: [
      'Export the GL Report from iSolved for the pay period (_GL_Report_YYYY-MM-DD.xlsx)',
      'Confirm Sheet1 has Date, Unit, Account, Amount, and Desc columns',
      'Already staged in keyword format? A sheet with a Layout row and Detail rows works as-is',
      'Verify amounts are signed: positive for debits, negative for credits',
      'Check that the FINAL sheet has Company ID (e.g. 81) — this is read automatically',
      'Confirm the entry balances to zero before uploading',
    ],
    keyRules: [
      { label: 'Source sheet', detail: 'Prefers the tabular data sheet (Sheet1). If there is none, reads a keyword sheet directly: Layout row defines the columns, Detail rows carry the lines.' },
      { label: 'Project coding', detail: 'Project Type / Project Id columns pass through when the source has them.' },
      { label: 'Signed amounts', detail: 'Amounts pass through as-is: positive = debit, negative = credit.' },
      { label: '$0 lines', detail: 'Dropped automatically — payroll exports carry a $0 line for every tax/benefit not hit that period (FUTA, SUI, dental…). The count is shown on the review screen.' },
      { label: 'Balance', detail: 'All amounts must sum to zero. iSolved exports should already balance.' },
      { label: 'Company ID', detail: 'Pulled from the FINAL sheet keyword block (defaults to 81 if missing).' },
    ],
    columnGuide: [
      { column: 'date', description: 'Pay/accounting date (from iSolved export)', required: false },
      { column: 'unit', description: 'Business unit ID', required: true },
      { column: 'account', description: 'GL account number', required: true },
      { column: 'amount', description: 'Signed amount (+ debit, − credit)', required: true },
      { column: 'desc', description: 'Line description from iSolved', required: false },
    ],
    outputPreview: `Company ID | 81
Accounting Date | 20260617
Entry Type | JE
Entry Status | O
Remarks | Payroll Entry
Layout | Unit | Account | Amount | Remarks
Detail | 1 | 201240 | -40 | WISMED DIRECT/PAC PAYABLE
Detail | 105 | 700000 | 11841.85 | ADMINISTRATIVE SERVICES`,
    importSteps: [
      { title: 'Copy file to server', detail: 'Place the .xlsx or .txt file in <mv_home>/<oracle_sid>/interface/in/glentry' },
      { title: 'Open GL Interface', detail: 'In MV: GL Manager Functions → GL Interface Data Corrections' },
      { title: 'Submit batch job', detail: 'Run using control file GL8610XL' },
      { title: 'Review batch log', detail: 'Verify each pay period JE posted and balanced correctly.' },
    ],
  },
};
